// What a browser-run turn carries from before it (2026-11-01): the whole
// conversation as it was sent — every earlier turn's request, the model's own
// blocks, each tool call and its answer — so "undo that" knows what "that"
// was. It used to carry the last six lines of text, as the server-run chat
// still does.
//
// The conversation only grows: nothing sent is rewritten, because Claude
// Sonnet 5.5's thinking blocks are valid only after an unchanged history, and
// the prompt cache reads back only an unchanged prefix. The API clears old
// tool results itself (functions/features/chat/chatStep.js). The one edit is
// at a turn's start, when the conversation would leave the turn less than
// half of the step's size limit: the oldest whole turns go.
import LIMITS from '@shared-lib/app-actions/chatTurnLimits.json';

/** What earlier turns may take of a step's limits; the turn gets the rest. */
export const HISTORY_BUDGET = {
  maxMessages: LIMITS.maxMessages / 2,
  maxChars: LIMITS.maxChars / 2,
};

const isLine = (m) =>
  (m.role === 'user' || m.role === 'assistant') &&
  typeof m.content === 'string' &&
  m.content !== '';

/** A user message that starts a turn — not one carrying tool results. */
const startsTurn = (m) =>
  m.role === 'user' &&
  (typeof m.content === 'string' ||
    !m.content.some((block) => block.type === 'tool_result'));

/**
 * The conversation to send before a new message.
 * @param {{ transcript: Array, coveredId: string } | null} conversation
 *   the last browser-run turn's whole conversation, and the id of the chat
 *   message that turn ended on
 * @param {Array} messages  the chat as shown, before the new message
 * @returns {Array} API messages, starting on the user. What the chat shows
 *   after `coveredId` (a turn the server-run chat answered, a failed turn's
 *   error) follows as plain lines; a chat without `coveredId` (Delete Chat)
 *   starts over from what it shows.
 */
export function historyFor(conversation, messages) {
  const coveredAt = conversation
    ? messages.findIndex((m) => m.id === conversation.coveredId)
    : -1;
  const kept = coveredAt >= 0 ? conversation.transcript : [];
  const since = messages
    .slice(coveredAt + 1)
    .filter(isLine)
    .map(({ role, content }) => ({ role, content }));
  const history = [...kept, ...since];
  const first = history.findIndex(startsTurn);
  return first < 0 ? [] : history.slice(first);
}

/**
 * `history` within `budget`, dropping the oldest whole turns — never part of
 * one, so no tool result loses the call it answers.
 * @param {Array} history
 * @param {{ maxMessages: number, maxChars: number }} [budget]
 * @returns {Array} `history` itself when it fits; [] when not even its newest
 *   turn does
 */
export function trimToBudget(history, budget = HISTORY_BUDGET) {
  // JSON.stringify of the array: brackets, each message, a comma between.
  const sizes = history.map((m) => JSON.stringify(m).length + 1);
  let chars = 1 + sizes.reduce((sum, n) => sum + n, 0);
  for (let start = 0; start < history.length; start += 1) {
    const count = history.length - start;
    const fits = count <= budget.maxMessages && chars <= budget.maxChars;
    if (fits && startsTurn(history[start]))
      return start === 0 ? history : history.slice(start);
    chars -= sizes[start];
  }
  return [];
}
